import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  accionsApi,
  claus,
  esImatge,
  esOberta,
  useAccioActivacio,
  useAccions,
  useActivacions,
  useMissatges,
  type AccioDto,
  type ActivacioDto,
  type DestinatariDto,
  type MissatgeDto,
} from '@/api/activacions';
import type { ActivacioResumDto, MissatgeTempsReal, TipusActivacio, TipusArea } from '@/api/types';
import { useMe } from '@/permisos/MeContext';
import type { ContextAmbit } from '@/permisos/permisos';
import { useTopic } from '@/realtime/RealtimeContext';

type Traductor = (k: string, o?: Record<string, unknown>) => string;

/** Paràmetre d'URL que obre directament una pestanya del detall W-04 (p. ex. `?pestanya=xat`). */
export const PARAM_PESTANYA = 'pestanya';

/** Ruta del detall d'una activació oberta a la pestanya Xat. */
export function rutaXat(activacioId: number): string {
  return `/activacions/${activacioId}?${PARAM_PESTANYA}=xat`;
}

/** L'usuari està mirant el xat d'aquesta activació (W-04 a `/activacions/{id}?pestanya=xat`). */
export function esVeientXat(pathname: string, search: string, activacioId: number): boolean {
  return (
    new RegExp(`^/activacions/${activacioId}(/|$)`).test(pathname) &&
    new URLSearchParams(search).get(PARAM_PESTANYA) === 'xat'
  );
}

export function textAccio(ac: AccioDto, t: Traductor, ns = ''): string {
  const d = ac.detall ?? {};
  const k = (s: string) => `${ns}detall.accio.${s}`;
  const str = (v: unknown) => (v == null || v === '' ? null : String(v));
  if (ac.tipus === 'ESCALADA') {
    const base = [
      t(k('escaladaNivells'), {
        origen: str(d.nivellOrigen) ?? '?',
        desti: str(d.nivellDesti) ?? '?',
      }),
      ac.usuari ? t(k('per'), { usuari: ac.usuari }) : t(k('automatic')),
    ].join(' ');
    const desti = str(d.destinatarisNoms);
    const text = desti ? `${base} → ${desti}` : base;
    const comentari = str(d.comentari);
    return comentari ? `${text} · ${comentari}` : text;
  }
  if (ac.tipus === 'EXPERT_ACTIVAT') {
    const nom = str(d.destinatariNom);
    const base = t(k('EXPERT_ACTIVAT'), { usuari: ac.usuari ?? '—' });
    return nom ? `${base} → ${nom}` : base;
  }
  const usuari = ac.usuari ?? '—';
  return t(k(ac.tipus), { usuari, defaultValue: t(k('ALTRA'), { usuari }) });
}

/** Text del tipus de destinatari d'un nivell d'escalat, sense mostrar mai el codi en brut. */
export function etiquetaTipusDesti(tipus: string, t: Traductor): string {
  return t(`detall.tipusDesti.${tipus}`, { defaultValue: t('detall.tipusDesti.ALTRE') });
}

export function inicials(nom?: string | null): string {
  if (!nom) return '·';
  return nom
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

const COLORS_AVATAR = [
  'bg-blue',
  'bg-orange',
  'bg-green',
  'bg-purple',
  'bg-red',
  'bg-teal',
  'bg-indigo',
  'bg-pink',
] as const;

/** Color estable d'avatar derivat del nom (paleta dels mockups). */
export function colorAvatar(nom?: string | null): string {
  if (!nom) return 'bg-gray';
  let h = 0;
  for (const c of nom) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return COLORS_AVATAR[h % COLORS_AVATAR.length] ?? 'bg-blue';
}

export function useTempsRealActivacio(id: number | null | undefined) {
  const qc = useQueryClient();
  useTopic(id != null ? `activacio:${id}` : null, (m: MissatgeTempsReal) => {
    if (id == null) return;
    if (m.type === 'missatge.nou' || m.type === 'mencio' || m.type === 'risc') {
      void qc.invalidateQueries({ queryKey: claus.part(id, 'missatges') });
    } else if (m.type === 'activacio.missatge') {
      // Text (conversa) o imatge (acció NOTA): es refresquen tots dos fils.
      void qc.invalidateQueries({ queryKey: claus.part(id, 'missatges') });
      void qc.invalidateQueries({ queryKey: claus.part(id, 'accions') });
    } else if (m.type === 'accio.nova') {
      void qc.invalidateQueries({ queryKey: claus.part(id, 'accions') });
    } else if (m.type === 'destinatari.estat') {
      void qc.invalidateQueries({ queryKey: claus.part(id, 'destinataris') });
    } else {
      void qc.invalidateQueries({ queryKey: claus.activacio(id) });
    }
  });
}

export function useTempsRealTauler() {
  const qc = useQueryClient();
  useTopic('tauler', () => {
    void qc.invalidateQueries({ queryKey: claus.activacions });
  });
}

/** Destinataris que encara han de confirmar (els d'espera o no notificats no compten). */
export function pendentsDe(destinataris: DestinatariDto[]): DestinatariDto[] {
  return destinataris.filter(
    (d) => d.estat !== 'CONFIRMAT' && d.estat !== 'EN_ESPERA' && d.estat !== 'NO_NOTIFICAT',
  );
}

/** Reenviament de l'avís als destinataris pendents (franja d'estat i pestanya Persones). */
export function useReenviaPendents(a: ActivacioDto, destinataris: DestinatariDto[]) {
  const { pot } = useMe();
  const pendents = pendentsDe(destinataris);
  const reenvia = useAccioActivacio(a.id, () =>
    accionsApi.reenviar(
      a.id,
      pendents.map((d) => d.id),
      a.canals,
    ),
  );
  const potReenviar =
    esOberta(a.estat) &&
    pendents.length > 0 &&
    pot('cai.activacions', 'MODIFICAR', ctxActivacio(a));
  return { pendents, reenvia, potReenviar };
}

/** Color de la barra de confirmacions segons el tipus (mockup Centre de control). */
export function colorConfirmacions(a: {
  areaTipus?: TipusArea | null;
  tipus: TipusActivacio;
  confirmats: number;
  total: number;
}): string | undefined {
  if (a.total > 0 && a.confirmats >= a.total) return 'var(--green)';
  if (a.areaTipus === 'CRISI') return 'var(--purple)';
  if (a.tipus === 'AVIS') return 'var(--teal)';
  return undefined;
}

export function iniciDies(dies: number, ara: Date = new Date()): string {
  const d = new Date(ara);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - dies);
  return d.toISOString();
}

export function iniciMes(ara: Date = new Date()): string {
  return new Date(ara.getFullYear(), ara.getMonth(), 1).toISOString();
}

export function ctxActivacio(a: ActivacioDto | undefined): ContextAmbit | undefined {
  if (!a) return undefined;
  return {
    departamentId: a.departamentId ?? undefined,
    areaId: a.areaId ?? undefined,
    serveiId: a.serveiIds[0] ?? undefined,
  };
}

export interface TrosText {
  text: string;
  mencio: boolean;
}

export function trossejaMencions(text: string, noms: string[]): TrosText[] {
  const valids = noms.filter(Boolean).sort((a, b) => b.length - a.length);
  if (!valids.length) return [{ text, mencio: false }];
  const esc = valids.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(@(?:${esc.join('|')}))`, 'g');
  return text
    .split(re)
    .filter((p) => p !== '')
    .map((p) => ({ text: p, mencio: p.startsWith('@') && valids.includes(p.slice(1)) }));
}

export function tokenMencio(text: string): string | null {
  const m = /(?:^|\s)@([^\s@]*)$/.exec(text);
  return m ? (m[1] ?? '') : null;
}

/** Temps relatiu curt: «fa 4 min», «fa 1 h 22 min», «fa 2 dies». */
export function textFa(minuts: number, t: Traductor): string {
  if (minuts < 60) return t('temps.min', { min: minuts });
  if (minuts < 1440) return t('temps.hMin', { h: Math.floor(minuts / 60), min: minuts % 60 });
  return t('temps.dies', { count: Math.floor(minuts / 1440) });
}

export function minutsDes(iso: string | null | undefined, ara: number): number | null {
  if (!iso) return null;
  return Math.max(0, Math.round((ara - new Date(iso).getTime()) / 60000));
}

const pad2 = (n: number) => String(n).padStart(2, '0');

function desplacamentZona(instant: number, zona: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zona,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const g = (tipus: string) => Number(parts.find((p) => p.type === tipus)?.value ?? 0);
  return (
    Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute'), g('second')) - instant
  );
}

/**
 * Converteix el valor d'un `<input type="datetime-local">` (hora de paret, sense zona) a ISO 8601
 * amb el desplaçament de la zona indicada (per defecte Europe/Madrid, també en horari d'estiu).
 * Ex.: «2026-10-20T21:06» → «2026-10-20T21:06:00+02:00».
 */
export function isoAmbZona(local: string, zona = 'Europe/Madrid'): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local);
  if (!m) return local;
  const [y, mo, d, h, mi, se] = m.slice(1).map((x) => Number(x ?? 0)) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const comUtc = Date.UTC(y, mo - 1, d, h, mi, se);
  let despl = desplacamentZona(comUtc, zona);
  despl = desplacamentZona(comUtc - despl, zona);
  const minuts = Math.round(despl / 60000);
  const abs = Math.abs(minuts);
  const signe = minuts >= 0 ? '+' : '-';
  return `${y}-${pad2(mo)}-${pad2(d)}T${pad2(h)}:${pad2(mi)}:${pad2(se)}${signe}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

/** Activacions obertes (OBERTA + EN_CURS) sense límit de dates, ordenades per data. */
export function useObertes() {
  const obertes = useActivacions({ estat: 'OBERTA', size: 100 });
  const enCurs = useActivacions({ estat: 'EN_CURS', size: 100 });
  const items = useMemo(() => {
    const vistos = new Map<number, ActivacioResumDto>();
    for (const a of [...(obertes.data?.items ?? []), ...(enCurs.data?.items ?? [])]) {
      if (esOberta(a.estat)) vistos.set(a.id, a);
    }
    return [...vistos.values()].sort((x, y) => {
      if (x.critica !== y.critica) return x.critica ? -1 : 1;
      return y.createdAt.localeCompare(x.createdAt);
    });
  }, [obertes.data, enCurs.data]);
  return {
    items,
    isLoading: obertes.isLoading || enCurs.isLoading,
    isError: obertes.isError && enCurs.isError,
    refetch: () => {
      void obertes.refetch();
      void enCurs.refetch();
    },
  };
}

/** Entrada del xat: un missatge de la conversa o una imatge (acció NOTA amb adjunt). */
export type EntradaXat =
  | { clau: string; at: string; accio: AccioDto }
  | { clau: string; at: string; missatge: MissatgeDto };

/**
 * Entrades del xat d'una activació en ordre cronològic: els missatges de `/missatges` i les
 * imatges (accions NOTA amb `adjuntId`). Les notes sense imatge són activitat, no xat.
 */
export function useEntradesXat(id: number): { entrades: EntradaXat[]; carregant: boolean } {
  const accions = useAccions(id);
  const missatges = useMissatges(id);
  const entrades: EntradaXat[] = [
    ...(accions.data ?? [])
      .filter(esImatge)
      .map((ac) => ({ clau: `a-${ac.id}`, at: ac.at, accio: ac })),
    ...(missatges.data ?? []).map((m, i) => ({
      clau: `m-${m.id ?? i}`,
      at: m.at ?? '',
      missatge: m,
    })),
  ].sort((x, y) => x.at.localeCompare(y.at));
  return { entrades, carregant: accions.isLoading || missatges.isLoading };
}
